/**
 * Home (`/`) — API-first developer home page.
 *
 * Three modes driven by `resolveHomeMode(usage, keys, audits)`
 * (topology.md §4, 05-pages.md §5a):
 *
 *   - `fresh`   — no key, no runs. Big linear Quickstart (create key
 *                  → run audit → watch → next steps).
 *   - `no-runs` — key exists but no audits. Same steps, step 1
 *                  collapsed.
 *   - `active`  — at least one run in the last 30 days. Compact dev
 *                  dashboard: 24h stats, recent audits peek, monitor
 *                  health, quick snippet card, usage cards.
 *
 * Unpaid fade (essentials §2): `usage.plan === null` ⇒ fade Monitors
 * + Seats usage cards with an upsell link. Phase 8 of the Polar
 * migration dropped the `subscription_ended_at` column — Polar's
 * terminal webhook writes `plan: null` directly, so a lapsed sub
 * funnels through the same signal.
 */
import { AuditsTable } from '@/components/primitives/AuditsTable';
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import { EmptyState } from '@/components/primitives/EmptyState';
import { ErrorState } from '@/components/primitives/ErrorState';
import { RunItPanel } from '@/components/primitives/RunItPanel';
import { UsageCard } from '@/components/primitives/UsageCard';
import { Island } from '@/components/shell/Island';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import type { ApiKey, AuditResponse, UsageResponse } from '@/lib/api-types';
import { formatScore, relativeTime, scoreColor } from '@/lib/format';
import {
  useAudit,
  useAudits,
  useCreateKey,
  useKeys,
  useMonitors,
  useUsage,
  useWorkspace,
} from '@/lib/queries';
import { pickFatalError } from '@/lib/query-errors';
import { cn } from '@/lib/utils';
import {
  BookOpen,
  CheckCircle,
  Key as KeyIcon,
  Plus,
  Rocket,
  Users,
  WebhooksLogo,
  XCircle,
} from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useMemo, useState } from 'react';

export const Route = createFileRoute('/_app/')({
  component: HomePage,
});

type HomeMode = 'fresh' | 'no-runs' | 'active' | 'loading';

function resolveHomeMode(
  usageLoaded: boolean,
  keys: ApiKey[] | undefined,
  audits: AuditResponse[] | undefined,
): HomeMode {
  if (!usageLoaded || keys === undefined || audits === undefined) return 'loading';
  const hasKey = keys.length > 0;
  const thirtyDays = 30 * 86400_000;
  const hasRecentRun = audits.some(
    (a) => Date.now() - new Date(a.created_at).getTime() < thirtyDays,
  );
  if (!hasKey && !hasRecentRun) return 'fresh';
  if (!hasRecentRun) return 'no-runs';
  return 'active';
}

function HomePage(): ReactElement {
  const workspace = useWorkspace();
  const usage = useUsage();
  const keys = useKeys();
  const audits = useAudits({ limit: 10 });

  const mode = resolveHomeMode(usage.isSuccess, keys.data, audits.data?.audits);

  const orgName = workspace.data?.organisations.find((o) => o.role)?.name ?? 'your workspace';

  // Any of the four queries failing blocks the composite view — we
  // can't choose between fresh / no-runs / active without them.
  // QueryCache already fires the toast; the in-page ErrorState gives
  // the user a "try again" anchor instead of leaving the skeleton
  // spinning forever.
  //
  // Transient errors that the QueryClient is already retrying under
  // the hood (429 rate-limit, 5xx transient) do NOT count as a fatal
  // firstError — they flap while react-query burns its retry budget.
  // If they still fail at the tail, `isRefetching` clears and the
  // error becomes fatal, which flips us back to ErrorState. Checking
  // `isFetching` keeps the skeleton up during the retry window
  // instead of flashing a full-page error on the first attempt.
  const firstError = pickFatalError(usage, keys, audits, workspace);

  return (
    <Island title={mode === 'active' ? `Welcome back to ${orgName}` : 'Welcome'}>
      {firstError && mode === 'loading' ? (
        <ErrorState
          title="We couldn't load your dashboard"
          error={firstError}
          retry={() => {
            void workspace.refetch();
            void usage.refetch();
            void keys.refetch();
            void audits.refetch();
          }}
        />
      ) : (
        <>
          {mode === 'loading' && <HomeSkeleton />}
          {mode === 'fresh' && <QuickstartFlow hasKey={false} />}
          {mode === 'no-runs' && <QuickstartFlow hasKey />}
          {mode === 'active' && usage.data && audits.data && (
            <ActiveDashboard usage={usage.data} audits={audits.data.audits} />
          )}
        </>
      )}
    </Island>
  );
}

// -----------------------------------------------------------------
// Loading
// -----------------------------------------------------------------

function HomeSkeleton(): ReactElement {
  return (
    <div className="space-y-6">
      <Skeleton className="h-32 w-full" />
      <div className="grid gap-4 sm:grid-cols-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

// -----------------------------------------------------------------
// Fresh / no-runs: linear quickstart
// -----------------------------------------------------------------

function QuickstartFlow({ hasKey }: { hasKey: boolean }): ReactElement {
  const [createdKey, setCreatedKey] = useState<{ prefix: string; secret?: string } | null>(null);
  const [runAuditId, setRunAuditId] = useState<string | null>(null);

  // Derive the "current" key snapshot: either the freshly created one
  // (we can show the secret once), or the first existing key (prefix
  // only, we never re-reveal).
  const keys = useKeys();
  const firstKeyPrefix = keys.data?.[0]?.prefix;

  const step1Done = hasKey || createdKey !== null;
  const step2Done = runAuditId !== null;

  return (
    <div className="space-y-6">
      <header className="max-w-2xl space-y-2">
        <Badge variant="secondary" size="sm">
          Quickstart
        </Badge>
        <h2 className="text-2xl font-semibold leading-tight text-[var(--color-ink)]">
          Ship your first Core Web Vitals audit in under a minute.
        </h2>
        <p className="text-sm text-[var(--color-mute)]">
          Four short steps — mint a key, hit our API, watch the run land. Copy-paste the snippet
          into your own tooling when you're done.
        </p>
      </header>

      <QuickstartStep
        index={1}
        title="Create your first API key"
        done={step1Done}
        collapsed={step1Done && !createdKey}
      >
        {step1Done && !createdKey ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-line)] bg-[var(--color-paper-alt)]/40 px-4 py-3 text-sm">
            <div className="flex items-center gap-2 text-[var(--color-ink)]">
              <KeyIcon aria-hidden="true" size={16} className="text-[var(--color-mute)]" />
              <span className="font-mono text-xs">{firstKeyPrefix ?? 'sk_live_•••'}•••</span>
              <span className="text-[var(--color-mute)]">
                — existing key (we never re-reveal secrets)
              </span>
            </div>
            <Button variant="ghost" size="sm" render={<Link to="/settings/keys" />}>
              Manage keys →
            </Button>
          </div>
        ) : createdKey ? (
          <CreatedKeyReveal prefix={createdKey.prefix} secret={createdKey.secret} />
        ) : (
          <CreateKeyAction onCreated={setCreatedKey} />
        )}
      </QuickstartStep>

      <QuickstartStep index={2} title="Run your first audit" done={step2Done}>
        <RunItPanel
          defaultUrl="https://example.com"
          apiKey={createdKey?.secret ?? firstKeyPrefix}
          onAudit={(a) => setRunAuditId(a.id)}
        />
      </QuickstartStep>

      <QuickstartStep index={3} title="Watch the audit complete" done={false}>
        {runAuditId ? (
          <WatchAudit auditId={runAuditId} />
        ) : (
          <p className="text-sm text-[var(--color-mute)]">
            Run a snippet above and we'll show the live status here.
          </p>
        )}
      </QuickstartStep>

      <QuickstartStep index={4} title="Next steps" done={false}>
        <div className="grid gap-3 sm:grid-cols-3">
          <NextStepCard
            icon={BookOpen}
            title="Install an SDK"
            description="Official Node + Python helpers that mirror the REST API."
            to="/docs/sdks"
          />
          <NextStepCard
            icon={WebhooksLogo}
            title="Set up a webhook"
            description="Get a signed callback every time an audit finishes."
            to="/settings/webhooks"
          />
          <NextStepCard
            icon={Users}
            title="Invite a teammate"
            description="Share access, keep audits under one workspace."
            to="/settings/members"
          />
        </div>
      </QuickstartStep>
    </div>
  );
}

function QuickstartStep({
  index,
  title,
  done,
  collapsed = false,
  children,
}: {
  index: number;
  title: string;
  done: boolean;
  collapsed?: boolean;
  children: ReactElement | ReactElement[] | string;
}): ReactElement {
  return (
    <section
      className={cn(
        'rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] transition-shadow',
        collapsed ? 'px-4 py-3' : 'p-5',
      )}
    >
      <header className="flex items-center gap-3">
        <div
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
            done
              ? 'bg-[var(--color-success)]/15 text-[var(--color-success)]'
              : 'bg-[var(--color-paper-alt)] text-[var(--color-ink-soft)]',
          )}
          aria-hidden="true"
        >
          {done ? <CheckCircle size={14} weight="bold" /> : index}
        </div>
        <h3 className="text-base font-semibold text-[var(--color-ink)]">{title}</h3>
      </header>
      <div className={cn(collapsed ? 'mt-2' : 'mt-4')}>{children}</div>
    </section>
  );
}

function CreateKeyAction({
  onCreated,
}: {
  onCreated: (k: { prefix: string; secret: string }) => void;
}): ReactElement {
  const mutation = useCreateKey();

  async function onClick() {
    const res = await mutation.mutateAsync({
      name: 'Default',
      scopes: ['snapshots:write', 'snapshots:read', 'metrics:read'],
    });
    onCreated({ prefix: res.key.prefix, secret: res.secret });
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--color-mute)]">
        We'll create a key named <span className="font-mono text-xs">Default</span> with{' '}
        <span className="font-mono text-xs">snapshots:write</span>,{' '}
        <span className="font-mono text-xs">snapshots:read</span>, and{' '}
        <span className="font-mono text-xs">metrics:read</span> scopes.
      </p>
      <Button onClick={onClick} loading={mutation.isPending}>
        <Plus aria-hidden="true" />
        Create API key
      </Button>
    </div>
  );
}

function CreatedKeyReveal({
  prefix,
  secret,
}: {
  prefix: string;
  secret?: string;
}): ReactElement {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 px-3 py-2 text-xs text-[var(--color-ink)]">
        Copy this secret now — we won't show it again. Treat it like a password.
      </div>
      <CodeSnippet title="API key" code={secret ?? prefix} />
      <p className="text-xs text-[var(--color-mute)]">
        Prefix <span className="font-mono">{prefix}</span> — used to identify the key in logs.
      </p>
    </div>
  );
}

function WatchAudit({ auditId }: { auditId: string }): ReactElement {
  const audit = useAudit(auditId);
  const status = audit.data?.status;

  if (!audit.data) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--color-mute)]">
        <Spinner className="size-4" />
        Dispatching…
      </div>
    );
  }

  const done = status === 'success' || status === 'error' || status === 'timeout';

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-line)] bg-[var(--color-paper-alt)]/40 px-4 py-3">
      <div className="flex items-center gap-3">
        {status === 'success' ? (
          <CheckCircle size={20} weight="fill" className="text-[var(--color-success)]" />
        ) : status === 'error' || status === 'timeout' ? (
          <XCircle size={20} weight="fill" className="text-[var(--color-danger)]" />
        ) : (
          <Spinner className="size-5" />
        )}
        <div>
          <div className="text-sm font-medium text-[var(--color-ink)]">{audit.data.url}</div>
          <div className="text-xs text-[var(--color-mute)]">
            {status} · {relativeTime(audit.data.created_at)}
          </div>
        </div>
      </div>
      {done && (
        <Button
          size="sm"
          variant="outline"
          render={<Link to="/settings/logs/$auditId" params={{ auditId: audit.data.id }} />}
        >
          View details →
        </Button>
      )}
    </div>
  );
}

function NextStepCard({
  icon: IconCmp,
  title,
  description,
  to,
}: {
  icon: typeof Rocket;
  title: string;
  description: string;
  to: string;
}): ReactElement {
  return (
    <Link
      to={to}
      className="flex flex-col gap-2 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4 transition-colors hover:border-[var(--color-accent-brand)]/60 hover:bg-[var(--color-paper-alt)]/40"
    >
      <IconCmp
        aria-hidden="true"
        size={24}
        weight="duotone"
        className="text-[var(--color-accent-brand)]"
      />
      <h4 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h4>
      <p className="text-xs text-[var(--color-mute)]">{description}</p>
    </Link>
  );
}

// -----------------------------------------------------------------
// Active dashboard
// -----------------------------------------------------------------

function ActiveDashboard({
  usage,
  audits,
}: {
  usage: UsageResponse;
  audits: AuditResponse[];
}): ReactElement {
  const isUnpaid = usage.plan === null;
  const recent = audits.slice(0, 5);

  // 24h stats.
  const stats = useMemo(() => {
    const cutoff = Date.now() - 86400_000;
    const last24 = audits.filter((a) => new Date(a.created_at).getTime() >= cutoff);
    const success = last24.filter((a) => a.status === 'success').length;
    const rate = last24.length > 0 ? Math.round((success / last24.length) * 100) : null;
    const lcpValues = last24
      .map((a) => a.metrics?.lcp_ms ?? null)
      .filter((v): v is number => v != null);
    const p75Lcp =
      lcpValues.length > 0
        ? (lcpValues.sort((a, b) => a - b)[Math.floor(lcpValues.length * 0.75)] ?? null)
        : null;
    return { total: last24.length, rate, p75Lcp };
  }, [audits]);

  return (
    <div className="space-y-6">
      {isUnpaid && <UnpaidBanner />}

      {/* Top strip: 24h stats */}
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label="Audits (24h)" value={stats.total} />
        <StatTile
          label="Success rate"
          value={stats.rate == null ? '—' : `${stats.rate}%`}
          tone={
            stats.rate == null
              ? 'muted'
              : stats.rate >= 95
                ? 'success'
                : stats.rate >= 80
                  ? 'warning'
                  : 'danger'
          }
        />
        <StatTile
          label="p75 LCP (24h)"
          value={stats.p75Lcp == null ? '—' : `${Math.round(stats.p75Lcp)}ms`}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {/* Left column */}
        <div className="space-y-4">
          <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
            <header className="flex items-center justify-between border-b border-[var(--color-line-soft)] px-4 py-3">
              <h3 className="text-sm font-semibold text-[var(--color-ink)]">Recent audits</h3>
              <Button variant="ghost" size="sm" render={<Link to="/settings/logs" />}>
                View all →
              </Button>
            </header>
            <div className="p-2">
              {recent.length === 0 ? (
                <EmptyState
                  icon={Rocket}
                  title="No audits yet"
                  description="Run one from a snippet or schedule a monitor."
                  action={
                    <Button render={<Link to="/monitors/new" />}>
                      <Plus aria-hidden="true" />
                      New monitor
                    </Button>
                  }
                />
              ) : (
                <AuditsTable audits={recent} variant="peek" />
              )}
            </div>
          </section>

          <MonitorHealth />
        </div>

        {/* Right column */}
        <aside className="space-y-4">
          <UsageCard
            title="Audits MTD"
            value={usage.mtd_on_demand}
            limit={usage.mtd_on_demand_limit}
            hint={
              usage.mtd_on_demand_limit != null
                ? 'Resets on the 1st of next month.'
                : 'No usage limit on your plan.'
            }
          />
          <UsageCard
            title="Monitors"
            value={usage.active_daily_monitors + usage.active_hourly_monitors}
            limit={
              sumLimit(usage.active_daily_monitors_limit, usage.active_hourly_monitors_limit) ??
              null
            }
            faded={isUnpaid}
            upsellHref="/settings/billing"
          />
          <UsageCard
            title="Seats"
            value={usage.members_count}
            limit={usage.members_limit}
            faded={isUnpaid}
            upsellHref="/settings/billing"
          />
          <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
            <div className="mb-3 text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
              Quick snippet
            </div>
            <CodeSnippet
              code={
                'curl https://api.clickweave.co/v1/audits \\\n' +
                "  -H 'Authorization: Bearer $API_KEY' \\\n" +
                "  -H 'Content-Type: application/json' \\\n" +
                '  -d \'{"url":"https://example.com"}\''
              }
            />
          </section>
        </aside>
      </div>
    </div>
  );
}

function StatTile({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'muted';
}): ReactElement {
  const toneCls = {
    default: 'text-[var(--color-ink)]',
    success: scoreColor(95),
    warning: scoreColor(70),
    danger: scoreColor(10),
    muted: 'text-[var(--color-mute)]',
  }[tone];
  return (
    <div className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] px-4 py-3">
      <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
        {label}
      </div>
      <div className={cn('mt-1 text-2xl font-semibold leading-none', toneCls)}>{value}</div>
    </div>
  );
}

function UnpaidBanner(): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 px-4 py-3">
      <div className="text-sm">
        <strong className="text-[var(--color-ink)]">You're on the free tier.</strong>{' '}
        <span className="text-[var(--color-mute)]">
          Monitors + seats are capped. Upgrade to unlock.
        </span>
      </div>
      <Button size="sm" render={<Link to="/settings/billing" />}>
        View plans →
      </Button>
    </div>
  );
}

function MonitorHealth(): ReactElement {
  const monitors = useMonitors();
  if (monitors.isLoading) {
    return <Skeleton className="h-24 w-full" />;
  }
  const items = monitors.data ?? [];
  if (items.length === 0) return <></>;

  return (
    <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center justify-between border-b border-[var(--color-line-soft)] px-4 py-3">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">Monitor health</h3>
        <Button variant="ghost" size="sm" render={<Link to="/monitors" />}>
          Manage →
        </Button>
      </header>
      <ul className="divide-y divide-[var(--color-line-soft)]">
        {items.slice(0, 4).map((m) => (
          <li key={m.site_uuid} className="flex items-center justify-between px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-[var(--color-ink)]">
                {m.site_name}
              </div>
              <div className="truncate text-xs text-[var(--color-mute)]">{m.site_url}</div>
            </div>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                m.monitoring_enabled && m.schedule
                  ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
                  : 'bg-[var(--color-paper-alt)] text-[var(--color-ink-soft)]',
              )}
            >
              {m.monitoring_enabled && m.schedule ? m.schedule : 'paused'}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function sumLimit(a: number | null, b: number | null): number | null {
  if (a == null && b == null) return null;
  return (a ?? 0) + (b ?? 0);
}
